import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import THEME from 'theme';
import { Card, Price, TickedDiscountItem } from 'components';
import { Button, Heading } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';
import {
  FunnelType,
  GovernmentCharges,
  PaymentFrequency,
} from 'scenes/common/types';
import { DEFAULT_HEADING_SIZES } from 'scenes/common/helpers/fonts';
import { toPercentage } from 'scenes/common/helpers/percentage';
import { DEFAULT_ONLINE_CAMPAIGN } from 'scenes/common/helpers/insuranceInfo';
import { suppressOfferApplied } from 'scenes/motor/helpers/campaign';
import { useIsLargeScreen } from 'scenes/common/hooks/useMedia';

import { GovernmentChargesModal } from './GovernmentChargesModal';

const PriceWrapper = styled.div.attrs({
  'data-testid': 'quote-price-wrapper',
})`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

const RedTextWrapper = styled.div<{ rightAligned?: boolean }>`
  p {
    color: ${THEME.LINK.DEFAULT} !important;
    ${(props) => props.rightAligned && 'text-align: right;'}
  }
`;

const ProductTypeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const PriceCard: FunctionComponent<{
  frequency: PaymentFrequency;
  coverType: string;
  selected: boolean;
  amount: string;
  footnoteText?: string;
  role?: string;
  onClick: () => void;
  onSwitchPaymentFrequency?: () => void;
  showGovernmentCharges?: boolean;
  yearlyGovernmentCharges?: GovernmentCharges;
  monthlyGovernmentCharges?: GovernmentCharges;
  ariaLive?: 'off' | 'polite' | 'assertive';
  productType?: FunnelType;
}> = ({
  frequency,
  coverType,
  selected,
  amount,
  onClick,
  footnoteText,
  role,
  onSwitchPaymentFrequency,
  showGovernmentCharges,
  yearlyGovernmentCharges,
  monthlyGovernmentCharges,
  ariaLive,
  productType,
}) => {
  const [showModal, setShowModal] = useState(false);
  const isLargeScreen = useIsLargeScreen();

  return (
    <>
      <Card
        id={frequency}
        selected={selected}
        justify
        onClick={onClick}
        role={role}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(ariaLive && { ariaLive })}
      >
        <ProductTypeWrapper>
          <div>
            <Heading
              size={2}
              margin={{ bottom: 0 }}
              fontSize={DEFAULT_HEADING_SIZES}
            >
              Pay {frequency}
            </Heading>
            <Heading
              size={3}
              margin={{ bottom: 0 }}
              fontSize={{ md: 20, xs: 18 }}
            >
              {coverType}
            </Heading>
          </div>
          {!isLargeScreen && productType === FunnelType.Motor && (
            <TickedDiscountItem
              campaignDescription={`${toPercentage(
                DEFAULT_ONLINE_CAMPAIGN.discount
              )} online discount applied`}
              suppressOfferApplied={suppressOfferApplied}
            />
          )}
        </ProductTypeWrapper>
        <PriceWrapper>
          <Price amount={amount} footnoteSymbol="*" />
          {footnoteText && (
            <TextSmall margin={{ top: 0 }}>{footnoteText}</TextSmall>
          )}
          {onSwitchPaymentFrequency && (
            <Button link margin={{ top: 0 }} onClick={onSwitchPaymentFrequency}>
              <RedTextWrapper>
                <TextSmall margin={{ top: 0 }}>
                  Switch to{' '}
                  {frequency === PaymentFrequency.Annually
                    ? PaymentFrequency.Monthly
                    : PaymentFrequency.Annually}
                </TextSmall>
              </RedTextWrapper>
            </Button>
          )}
          {showGovernmentCharges && (
            <Button
              link
              onClick={() => setShowModal(true)}
              margin={{ top: 3, bottom: 1 }}
            >
              <RedTextWrapper rightAligned>
                <TextSmall>Show government charges</TextSmall>
              </RedTextWrapper>
            </Button>
          )}
        </PriceWrapper>
      </Card>
      {showGovernmentCharges &&
      yearlyGovernmentCharges &&
      monthlyGovernmentCharges ? (
        <GovernmentChargesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          yearlyGovernmentCharges={yearlyGovernmentCharges}
          monthlyGovernmentCharges={monthlyGovernmentCharges}
        />
      ) : (
        <></>
      )}
    </>
  );
};
